import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useFaq, FaqSettings, FaqItem, defaultFaqSettings } from '@/context/FaqContext';

export default function FaqAdminTab() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const { settings: globalSettings, updateSettings, resetSettings } = useFaq();
  const [settings, setSettings] = useState<FaqSettings>(globalSettings);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const innerCard = dark ? 'bg-[#0D1B2A] border border-white/5' : 'bg-gray-50 border border-gray-100';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[var(--accent)]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[var(--accent)]';
  const fieldCls = `w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`;
  const labelCls = `block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`;

  const publishedCount = settings.faqs.filter((f) => f.published).length;

  const filteredFaqs = activeCategory === 'All'
    ? settings.faqs
    : settings.faqs.filter((f) => f.category === activeCategory);

  const updateFaq = (id: string, updates: Partial<FaqItem>) => {
    setSettings((prev) => ({
      ...prev,
      faqs: prev.faqs.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    }));
  };

  const addFaq = () => {
    const newFaq: FaqItem = {
      id: `faq-${Date.now()}`,
      question: 'New Question',
      answer: 'Enter the answer here.',
      category: activeCategory === 'All' ? (settings.categories[0] || 'General') : activeCategory,
      published: false,
    };
    setSettings((prev) => ({ ...prev, faqs: [...prev.faqs, newFaq] }));
    setEditingId(newFaq.id);
  };

  const removeFaq = (id: string) => {
    setSettings((prev) => ({ ...prev, faqs: prev.faqs.filter((f) => f.id !== id) }));
    if (editingId === id) setEditingId(null);
  };

  const moveFaq = (id: string, dir: -1 | 1) => {
    setSettings((prev) => {
      const faqs = [...prev.faqs];
      const idx = faqs.findIndex((f) => f.id === id);
      const target = idx + dir;
      if (target < 0 || target >= faqs.length) return prev;
      [faqs[idx], faqs[target]] = [faqs[target], faqs[idx]];
      return { ...prev, faqs };
    });
  };

  const addCategory = () => {
    const trimmed = newCategory.trim();
    if (!trimmed || settings.categories.includes(trimmed)) return;
    setSettings((prev) => ({ ...prev, categories: [...prev.categories, trimmed] }));
    setNewCategory('');
    setShowAddCategory(false);
  };

  const removeCategory = (cat: string) => {
    setSettings((prev) => ({
      ...prev,
      categories: prev.categories.filter((c) => c !== cat),
      faqs: prev.faqs.map((f) => f.category === cat ? { ...f, category: prev.categories[0] || 'General' } : f),
    }));
    if (activeCategory === cat) setActiveCategory('All');
  };

  const handleSave = () => {
    updateSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    resetSettings();
    setSettings(defaultFaqSettings);
    setEditingId(null);
    setActiveCategory('All');
  };

  const editingFaq = editingId ? settings.faqs.find((f) => f.id === editingId) : null;

  return (
    <div className="space-y-5">
      {/* Page settings */}
      <div className={`${card} rounded-lg p-6 space-y-4`}>
        <h2 className={`font-bold text-sm ${text} flex items-center gap-2`}>
          <i className="ri-question-answer-line" style={{ color: 'var(--accent)' }}></i>
          FAQ Page Settings
        </h2>
        <div>
          <label className={labelCls}>Page Title</label>
          <input
            className={fieldCls}
            value={settings.pageTitle}
            onChange={(e) => setSettings((p) => ({ ...p, pageTitle: e.target.value }))}
          />
        </div>
        <div>
          <label className={labelCls}>Page Subtitle</label>
          <textarea
            rows={2}
            className={fieldCls}
            value={settings.pageSubtitle}
            onChange={(e) => setSettings((p) => ({ ...p, pageSubtitle: e.target.value }))}
          />
        </div>

        {/* Categories */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelCls}>Categories</label>
            <button
              onClick={() => setShowAddCategory(!showAddCategory)}
              className="flex items-center gap-1 text-xs font-bold cursor-pointer whitespace-nowrap"
              style={{ color: 'var(--accent)' }}
            >
              <i className="ri-add-line"></i> Add Category
            </button>
          </div>
          {showAddCategory && (
            <div className="flex gap-2 mb-3">
              <input
                className={`${fieldCls} flex-1`}
                placeholder="Category name..."
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCategory()}
              />
              <button
                onClick={addCategory}
                className="px-4 py-2 rounded-lg text-white text-xs font-bold cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                Add
              </button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {settings.categories.map((cat) => (
              <div
                key={cat}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${innerCard}`}
              >
                <span className={text}>{cat}</span>
                <button
                  onClick={() => removeCategory(cat)}
                  className="w-4 h-4 flex items-center justify-center text-red-400 hover:text-red-500 cursor-pointer shrink-0"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ List + Editor */}
      <div className="flex flex-col lg:flex-row gap-5">
        {/* Left: FAQ list */}
        <div className="lg:w-72 shrink-0 space-y-3">
          {/* Stats + Add */}
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-bold uppercase tracking-widest ${muted}`}>
                Questions ({settings.faqs.length})
              </p>
              <p className={`text-xs mt-0.5 ${muted}`}>{publishedCount} published</p>
            </div>
            <button
              onClick={addFaq}
              className="flex items-center gap-1.5 text-white text-xs font-bold px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              <i className="ri-add-line"></i> Add FAQ
            </button>
          </div>

          {/* Category filter */}
          <div className="flex flex-wrap gap-1.5">
            {['All', ...settings.categories].map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'text-white'
                    : dark ? 'bg-white/5 text-white/40 hover:text-white/70' : 'bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
                style={activeCategory === cat ? { backgroundColor: 'var(--accent)' } : {}}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* FAQ items */}
          <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
            {filteredFaqs.map((faq, idx) => (
              <div
                key={faq.id}
                onClick={() => setEditingId(faq.id)}
                className={`group flex items-start gap-2 p-3 rounded-lg cursor-pointer transition-all ${
                  editingId === faq.id
                    ? ''
                    : dark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                }`}
                style={editingId === faq.id ? { backgroundColor: 'color-mix(in srgb, var(--accent) 10%, transparent)', borderLeft: '3px solid var(--accent)' } : {}}
              >
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold truncate ${editingId === faq.id ? '' : text}`}
                    style={editingId === faq.id ? { color: 'var(--accent)' } : {}}>
                    {faq.question}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] ${muted}`}>{faq.category}</span>
                    <span className={`text-[10px] font-bold ${faq.published ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {faq.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); moveFaq(faq.id, -1); }}
                    className={`w-5 h-5 flex items-center justify-center rounded text-xs ${dark ? 'hover:bg-white/10 text-white/30' : 'hover:bg-gray-200 text-gray-400'}`}
                  >
                    <i className="ri-arrow-up-s-line"></i>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); moveFaq(faq.id, 1); }}
                    className={`w-5 h-5 flex items-center justify-center rounded text-xs ${dark ? 'hover:bg-white/10 text-white/30' : 'hover:bg-gray-200 text-gray-400'}`}
                  >
                    <i className="ri-arrow-down-s-line"></i>
                  </button>
                </div>
              </div>
            ))}
            {filteredFaqs.length === 0 && (
              <p className={`text-xs text-center py-6 ${muted}`}>No questions in this category</p>
            )}
          </div>
        </div>

        {/* Right: Editor */}
        <div className="flex-1">
          {editingFaq ? (
            <div className={`${card} rounded-lg p-6 space-y-4`}>
              <div className="flex items-center justify-between">
                <h3 className={`font-bold text-sm ${text}`}>Edit Question</h3>
                <div className="flex items-center gap-3">
                  {/* Published toggle */}
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${muted}`}>Published</span>
                    <button
                      onClick={() => updateFaq(editingFaq.id, { published: !editingFaq.published })}
                      className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${editingFaq.published ? 'bg-emerald-500' : dark ? 'bg-white/20' : 'bg-gray-200'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${editingFaq.published ? 'left-5' : 'left-0.5'}`}></span>
                    </button>
                  </div>
                  <button
                    onClick={() => removeFaq(editingFaq.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>

              <div>
                <label className={labelCls}>Category</label>
                <select
                  className={`${fieldCls} cursor-pointer`}
                  value={editingFaq.category}
                  onChange={(e) => updateFaq(editingFaq.id, { category: e.target.value })}
                >
                  {settings.categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Question *</label>
                <input
                  className={fieldCls}
                  value={editingFaq.question}
                  onChange={(e) => updateFaq(editingFaq.id, { question: e.target.value })}
                  placeholder="Enter the question..."
                />
              </div>

              <div>
                <label className={labelCls}>Answer *</label>
                <textarea
                  rows={6}
                  className={fieldCls}
                  value={editingFaq.answer}
                  onChange={(e) => updateFaq(editingFaq.id, { answer: e.target.value })}
                  placeholder="Enter the answer..."
                  maxLength={1000}
                />
                <p className={`text-xs mt-1 ${muted}`}>{editingFaq.answer.length}/1000 characters</p>
              </div>

              {/* Preview */}
              <div className={`rounded-lg p-4 ${innerCard}`}>
                <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${muted}`}>Preview</p>
                <p className={`text-sm font-semibold mb-2 ${text}`}>{editingFaq.question}</p>
                <p className={`text-xs leading-relaxed ${muted}`}>{editingFaq.answer}</p>
              </div>
            </div>
          ) : (
            <div className={`${card} rounded-lg p-12 flex flex-col items-center justify-center text-center`}>
              <div
                className="w-14 h-14 flex items-center justify-center rounded-xl mb-4"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}
              >
                <i className="ri-question-line text-2xl" style={{ color: 'var(--accent)' }}></i>
              </div>
              <p className={`font-bold text-sm ${text} mb-1`}>Select a question to edit</p>
              <p className={`text-xs ${muted}`}>Click any question from the list, or add a new one</p>
              <button
                onClick={addFaq}
                className="mt-4 flex items-center gap-2 text-white text-xs font-bold px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                <i className="ri-add-line"></i> Add New FAQ
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          onClick={handleReset}
          className={`flex items-center gap-2 font-bold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm border ${dark ? 'border-white/10 text-white/40 hover:text-white/70' : 'border-gray-200 text-gray-400 hover:text-gray-600'}`}
        >
          <i className="ri-refresh-line"></i> Reset to Default
        </button>
        <div className="flex items-center gap-3">
          {saved && (
            <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
              <i className="ri-check-line"></i> FAQ saved &amp; published!
            </div>
          )}
          <button
            onClick={handleSave}
            className="flex items-center gap-2 text-white font-bold px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
            style={{ background: 'var(--accent)' }}
          >
            <i className="ri-save-line"></i> Save &amp; Publish
          </button>
        </div>
      </div>
    </div>
  );
}
